import React, { useState } from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Badge from '@amzn/awsui-components-console/badge';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Popover from '@amzn/awsui-components-console/popover';
import Icon from '@amzn/awsui-components-console/icon';
import { TranslationProgress } from 'src/generated/graphql';
import styles from './TranslationProgressPanel.module.scss';

interface TranslationProgressPanelProps {
  translationProgress: TranslationProgress;
  /** When set, displays a variant badge next to the header (variant view only) */
  variantCxId?: string;
}

const TranslationProgressPanel: React.FC<TranslationProgressPanelProps> = ({
  translationProgress,
  variantCxId,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const completed = translationProgress.completedLocales.length;
  const inProgress = translationProgress.inProgressLocales.length;
  const failed = translationProgress.failedLocales.length;
  const total = completed + inProgress + failed;
  const percentComplete = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <Container
      header={
        <Header
          actions={
            <Button
              variant="normal"
              onClick={() => setShowDetails(!showDetails)}
              ariaLabel={
                showDetails
                  ? 'Hide translation details'
                  : 'Show translation details'
              }
              data-testid="progress-show-details"
            >
              {showDetails ? 'Hide details' : 'Show details'}
            </Button>
          }
        >
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            Translation Progress
            <Popover
              triggerType="text"
              header="Translation Progress"
              content="Shows translation completion status across all target locales for this string."
              triggerAriaLabel="Info about translation progress"
            >
              <Icon name="status-info" size="small" />
            </Popover>
            {variantCxId && (
              <Badge color="blue" data-testid="progress-variant-badge">
                Variant: {variantCxId}
              </Badge>
            )}
          </SpaceBetween>
        </Header>
      }
    >
      <div>
        <Box data-testid="progress-percent-label">
          {percentComplete}% complete
        </Box>
        {/* Slim custom bar matching the string group detail page. That bar has
            no label/description slots, so the text the Cloudscape ProgressBar
            used to render via `label` and `description` is rendered here
            explicitly so no information is lost. */}
        <Box variant="awsui-key-label" data-testid="progress-label">
          Translation completion
        </Box>
        <div
          className={styles.progressBarTrack}
          role="progressbar"
          aria-valuenow={percentComplete}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Translation progress: ${percentComplete}% complete`}
        >
          <div
            className={styles.progressBarFill}
            style={{ width: `${percentComplete}%` }}
          />
        </div>
        <Box
          variant="small"
          color="text-body-secondary"
          data-testid="progress-description"
        >
          {`${completed} of ${total} locales completed`}
        </Box>
      </div>
      {showDetails && (
        <Box margin={{ top: 'm' }}>
          <div data-testid="progress-stats">
            <ColumnLayout columns={3}>
              <Box textAlign="center" data-testid="stat-completed">
                <div className={styles.statSuccess}>
                  <Box
                    variant="awsui-value-large"
                    color="inherit"
                    data-testid="stat-num"
                  >
                    {completed}
                  </Box>
                </div>
                <Box variant="awsui-key-label">Locales Completed</Box>
              </Box>
              <Box textAlign="center" data-testid="stat-in-progress">
                <div className={styles.statWarning}>
                  <Box
                    variant="awsui-value-large"
                    color="inherit"
                    data-testid="stat-num"
                  >
                    {inProgress}
                  </Box>
                </div>
                <Box variant="awsui-key-label">In Progress</Box>
              </Box>
              <Box textAlign="center" data-testid="stat-failed">
                <div className={styles.statError}>
                  <Box
                    variant="awsui-value-large"
                    color="inherit"
                    data-testid="stat-num"
                  >
                    {failed}
                  </Box>
                </div>
                <Box variant="awsui-key-label">Failed</Box>
              </Box>
            </ColumnLayout>
          </div>
        </Box>
      )}
    </Container>
  );
};

export default TranslationProgressPanel;
